import React, { forwardRef, useCallback, useImperativeHandle, useMemo, useState, } from 'react';
import { HeaderBackButton } from '@react-navigation/elements';
import { View, TextInput, TouchableOpacity, Keyboard, InteractionManager, TouchableWithoutFeedback, } from 'react-native';
import Animated, { interpolateColor, useAnimatedScrollHandler, useAnimatedStyle, useSharedValue, } from 'react-native-reanimated';
import Spacer from 'design-system/Spacer';
import { rem } from 'design-system/values';
import { IconSearch } from 'design-system/icons';
import { NavigationHeader } from 'design-system';
import useStyles from 'hooks/useStyles';
import useColors from 'hooks/useColors';
import { sanitizeQuotes } from 'utils/text/sanitizeQuotes';
import { useAutoFocusInput } from 'hooks/useAutoFocusInput';
import useAppFrameDimensions from 'hooks/useAppFrameDimensions';
import Filters from './Filters';
import IconClose from './icons/IconClose';
import useKeyPress from './hooks/useKeyPress';
import { isWeb } from '../../../../constants';
import useCheckFilter from './hooks/useCheckFilter';
import useAmountHandlers from './hooks/useAmountHandlers';
import styleSet, { MARGIN_HORIZONTAL, CLEAR_TOUCH_ZONE_WIDTH } from './styles';
const ICON_SEARCH_SIZE = rem(24);
const ICON_SEARCH_LEFT_MARGIN_RAW = 16;
const ICON_SEARCH_RIGHT_MARGIN_RAW = 8;
const strings = {
    search: 'Search',
    cancel: 'Cancel',
};
const DEFAULT_STATE = {
    amountLowerValue: '',
    amountUpperValue: '',
};
const SearchHeaderFiltered = forwardRef(({ scrollY, searchValue, activeFilter, refSearchBar, refAmountLowerInput, refAmountUpperInput, currencySymbol, activeKeyboard, scrollViewRef, headerRight, cancelEdit, onToggleAmountFilter, onInputFocus, onChangeText, onCancel, onSelectFilter, onRemoveFilter, onInputBlur, onSearchValueChange, showSpecialKeyboard, children, clearEmptyTextInputFilters, }, ref) => {
    const scrollXValue = useSharedValue(0);
    const scrollAfterInteractions = useCallback((scroll) => {
        if (isWeb) {
            scroll();
            return;
        }
        InteractionManager.runAfterInteractions(scroll);
    }, []);
    const scrollHandler = useAnimatedScrollHandler((handle) => {
        scrollXValue.value = handle.contentOffset.x;
    }, []);
    const [state, setState] = useState(DEFAULT_STATE);
    const onSetDefaultAmount = useCallback((amount) => {
        setState((currentState) => ({ ...currentState, amountUpperValue: amount }));
    }, []);
    useImperativeHandle(ref, () => ({
        onSetDefaultAmount,
    }));
    const checkFilter = useCheckFilter(onSelectFilter);
    const closeScreen = useCallback(() => {
        Keyboard.dismiss();
        onCancel();
    }, [onCancel]);
    const handleChangeText = useCallback((textValue) => {
        const textWithoutFilter = checkFilter(sanitizeQuotes(textValue));
        onChangeText(textWithoutFilter);
    }, [checkFilter, onChangeText]);
    const clearSearch = useCallback(() => {
        setState(DEFAULT_STATE);
        onToggleAmountFilter();
        onRemoveFilter('CLEAR_FILTERS');
    }, [onToggleAmountFilter, onRemoveFilter]);
    useAutoFocusInput(refSearchBar);
    const handleClearPress = useCallback(() => {
        clearSearch();
        refSearchBar.current?.focus();
    }, [clearSearch, refSearchBar]);
    const onToggleSelectedFilter = useCallback((item) => {
        cancelEdit();
        if (Array.isArray(item)) {
            if (item[0].filterType === 'CATEGORY') {
                showSpecialKeyboard('KeyboardCategories');
            }
            else if (item[0].filterType === 'MERCHANT') {
                showSpecialKeyboard('KeyboardMerchants');
            }
            else if (item[0].filterType === 'TAG') {
                showSpecialKeyboard('KeyboardTags');
            }
            else if (item[0].filterType === 'ACCOUNT') {
                showSpecialKeyboard('KeyboardAccounts');
            }
        }
        else if (item.filterType === 'AMOUNT_UPPER') {
            refAmountUpperInput.current?.focus();
            showSpecialKeyboard('KeyboardAmountUpper');
        }
        else if (item.filterType === 'AMOUNT_LOWER') {
            refAmountLowerInput.current?.focus();
            showSpecialKeyboard('KeyboardAmountLower');
        }
        else if (item.filterType === 'DATES') {
            showSpecialKeyboard('KeyboardDates');
        }
    }, [cancelEdit, showSpecialKeyboard, refAmountUpperInput, refAmountLowerInput]);
    const onRemoveItem = useCallback((item) => {
        onRemoveFilter(item);
        if (item !== 'CLEAR_FILTERS' &&
            item !== 'CATEGORY' &&
            item !== 'MERCHANT' &&
            item !== 'TAG' &&
            item !== 'ACCOUNT') {
            if (item.filterType === 'AMOUNT_LOWER') {
                setState((currentState) => ({
                    ...currentState,
                    amountLowerValue: '',
                }));
            }
            if (item.filterType === 'AMOUNT_UPPER') {
                setState((currentState) => ({
                    ...currentState,
                    amountUpperValue: '',
                }));
            }
        }
    }, [onRemoveFilter]);
    const [onChangeAmountLower, onChangeAmountUpper, onResetLowerAmount, onResetUpperAmount, hideAmountInput] = useAmountHandlers(setState, onSelectFilter, onToggleAmountFilter, currencySymbol);
    const onKeyPress = useKeyPress(searchValue, activeFilter, onRemoveFilter, onResetLowerAmount, onResetUpperAmount);
    const handleInputFocus = useCallback(() => {
        hideAmountInput();
        onInputFocus();
        scrollAfterInteractions(() => {
            scrollViewRef.current?.scrollToEnd({ animated: true });
        });
    }, [hideAmountInput, onInputFocus, scrollAfterInteractions, scrollViewRef]);
    const handleTextInputBlur = useCallback(() => {
        onInputBlur();
    }, [onInputBlur]);
    const { width, paddingHorizontalStyle, sidebarWidth, paddingHorizontal } = useAppFrameDimensions(isWeb);
    let textInputWidth;
    const filterLength = Object.keys(activeFilter).length;
    const placeholder = activeFilter && !filterLength ? strings.search : '';
    let showCross = false;
    let showSearchIcon = true;
    if (activeFilter && filterLength) {
        showCross = true;
        showSearchIcon = false;
        textInputWidth = 0;
    }
    else {
        textInputWidth =
            width -
                MARGIN_HORIZONTAL * 2 -
                CLEAR_TOUCH_ZONE_WIDTH -
                ICON_SEARCH_SIZE -
                rem(ICON_SEARCH_LEFT_MARGIN_RAW + ICON_SEARCH_RIGHT_MARGIN_RAW);
    }
    if (searchValue) {
        showCross = true;
    }
    const styles = useStyles(styleSet);
    const colors = useColors();
    const searchBarAnimatedColorStyles = useAnimatedStyle(() => ({
        backgroundColor: interpolateColor(scrollY.value, [0, 40], [
            colors.cards.onDark,
            colors.background.dark,
        ]),
    }), [colors.background.dark, colors.cards.onDark, scrollY]);
    const textInput = useMemo(() => (<TextInput ref={refSearchBar} spellCheck={false} autoCorrect={false} value={searchValue} autoComplete="off" returnKeyType="search" onKeyPress={onKeyPress} placeholder={placeholder} onFocus={handleInputFocus} onBlur={handleTextInputBlur} onChangeText={handleChangeText} selectionColor={colors.text.selection} placeholderTextColor={colors.text.placeholder} style={[styles.searchInput, { width: textInputWidth }]}/>), [
        refSearchBar,
        searchValue,
        onKeyPress,
        placeholder,
        handleInputFocus,
        handleTextInputBlur,
        handleChangeText,
        colors.text.selection,
        colors.text.placeholder,
        styles.searchInput,
        textInputWidth,
    ]);
    const scrollToItem = useCallback((itemX) => {
        scrollAfterInteractions(() => {
            scrollViewRef.current?.scrollTo({
                animated: true,
                y: 0,
                x: scrollXValue.value + itemX - rem(32) - sidebarWidth - paddingHorizontal,
            });
        });
    }, [paddingHorizontal, scrollAfterInteractions, scrollViewRef, scrollXValue, sidebarWidth]);
    const focusTextInput = useCallback(() => {
        refSearchBar.current?.focus();
    }, [refSearchBar]);
    return (<NavigationHeader isInModal title="Transaction search" scrollY={scrollY} leftContent={<HeaderBackButton displayMode="minimal" onPress={closeScreen} style={styles.backChevron} tintColor={colors.elements.primary}/>} rightContent={headerRight}>
        <View style={paddingHorizontalStyle}>
          <View style={styles.searchHeaderRow}>
            <TouchableWithoutFeedback onPress={focusTextInput}>
              <Animated.View style={[styles.searchBar, searchBarAnimatedColorStyles]}>
                <Animated.ScrollView horizontal ref={scrollViewRef} onScroll={scrollHandler} keyboardShouldPersistTaps="always" showsHorizontalScrollIndicator={false} contentContainerStyle={[styles.scrollViewContentContainer, showSearchIcon && styles.withSearchIcon]}>
                  {showSearchIcon ? (<>
                      <Spacer w={ICON_SEARCH_LEFT_MARGIN_RAW}/>
                      <IconSearch />
                      <Spacer w={ICON_SEARCH_RIGHT_MARGIN_RAW}/>
                    </>) : null}
                  <Filters scrollY={scrollY} cancelEdit={cancelEdit} activeFilter={activeFilter} amountLowerValue={state.amountLowerValue} amountUpperValue={state.amountUpperValue} refAmountLowerInput={refAmountLowerInput} refAmountUpperInput={refAmountUpperInput} onChangeAmountLower={onChangeAmountLower} onChangeAmountUpper={onChangeAmountUpper} onToggleSelectedFilter={onToggleSelectedFilter} onRemoveItem={onRemoveItem} scrollToItem={scrollToItem} onSearchValueChange={onSearchValueChange} activeKeyboard={activeKeyboard} clearEmptyTextInputFilters={clearEmptyTextInputFilters}/>
                  {textInput}
                </Animated.ScrollView>
                {showCross ? (<TouchableOpacity onPress={handleClearPress} style={styles.closeIcon}>
                    <IconClose />
                  </TouchableOpacity>) : null}
              </Animated.View>
            </TouchableWithoutFeedback>
          </View>
          {children}
        </View>
      </NavigationHeader>);
});
export default React.memo(SearchHeaderFiltered);
